import { SubscriptionStatus, UserSubscription } from 'types/subscription';

export const STRIPE_ENDED_STATUSES = [
  'canceled',
  'unpaid',
  'incomplete_expired',
] as const;

export type StripeEndedStatus = typeof STRIPE_ENDED_STATUSES[number];

/** Sticky + profile status billing-attention reasons, highest priority first. */
export const BILLING_ATTENTION_REASONS = [
  'unpaid',
  'past_due',
  'overdue_invoice',
] as const;

export type BillingAttentionReason = typeof BILLING_ATTENTION_REASONS[number];

function isSendInvoiceCollection(
  subscription?: UserSubscription | null,
): boolean {
  return subscription?.stripeCollectionMethod === 'send_invoice';
}

/**
 * Invoice billing past due: Stripe marks the subscription `past_due` when a
 * `send_invoice` invoice is unpaid after its due date.
 */
export function isOverdueInvoice(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  const stripeStatus = subscription.stripeSubscriptionStatus?.toLowerCase();
  if (stripeStatus === 'overdue_invoice') return true;
  return stripeStatus === 'past_due' && isSendInvoiceCollection(subscription);
}

export function isPaidPlanTrialPeriod(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription?.subscriptionPlan) return false;

  if (subscription.planId === SubscriptionStatus.TRIAL) return false;
  if (subscription.stripeSubscriptionStatus === 'trialing') return true;
  if (subscription.status === SubscriptionStatus.TRIAL) return true;
  return false;
}

export function isStripeSubscriptionFullyEnded(
  subscription?: UserSubscription | null,
): boolean {
  const status = subscription?.stripeSubscriptionStatus;
  if (!status) return false;
  return (STRIPE_ENDED_STATUSES as readonly string[]).includes(status);
}

/** App-level cancelled when Stripe has not fully ended the subscription. */
export function isLegacyAppCancelled(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (isStripeSubscriptionFullyEnded(subscription)) return false;
  return (
    subscription.status === SubscriptionStatus.CANCELLED &&
    subscription.subscriptionId !== SubscriptionStatus.MIGRATED
  );
}

/**
 * User canceled but still has access until period end (no renewal).
 * Billing attention for routine payment issues is suppressed in this state
 * (overdue invoice / pause / unpaid are handled separately).
 */
export function isUserScheduledCancel(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (isLegacyAppCancelled(subscription)) return true;
  return (
    subscription.stripeCancelAtPeriodEnd === true &&
    !isStripeSubscriptionFullyEnded(subscription)
  );
}

/**
 * Highest-priority billing attention for sticky + profile status banners.
 * Driven by stripeSubscriptionStatus + stripeCollectionMethod.
 * Suppressed for scheduled cancel except unpaid, pause, and overdue invoice.
 */
export function getBillingAttentionReason(
  subscription?: UserSubscription | null,
): BillingAttentionReason | null {
  if (!subscription) return null;

  const stripeStatus = subscription.stripeSubscriptionStatus?.toLowerCase();

  if (stripeStatus === 'unpaid') return 'unpaid';

  if (isOverdueInvoice(subscription)) return 'overdue_invoice';

  if (isUserScheduledCancel(subscription)) return null;

  if (stripeStatus === 'past_due') return 'past_due';

  return null;
}

/**
 * True when access was revoked for non-payment (no remaining paid period).
 * "Valid until" should show "-" in this case — not a period-end date.
 */
export function isCancelledWithoutRemainingAccess(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (subscription.status !== SubscriptionStatus.CANCELLED) return false;

  if (
    subscription.pausedForNonPaymentAt ||
    subscription.cancelledForNonPaymentAt
  ) {
    return true;
  }

  // CANCELLED with no period-end fields → nothing left to show as valid-until.
  if (!subscription.stripeCurrentPeriodEnd && !subscription.endDate) {
    return true;
  }

  return false;
}

/**
 * Cancelled after unpaid/overdue invoice: resubscribe checkout must use
 * direct payment. Invoice option stays visible but disabled.
 * Once they reactivate on card, they are a normal subscriber again.
 */
export function mustResubscribeWithDirectPayment(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (subscription.status !== SubscriptionStatus.CANCELLED) return false;

  if (
    subscription.pausedForNonPaymentAt ||
    subscription.cancelledForNonPaymentAt
  ) {
    return true;
  }

  const stripeStatus = subscription.stripeSubscriptionStatus?.toLowerCase();
  return stripeStatus === 'overdue_invoice' || stripeStatus === 'unpaid';
}

export class InvoiceBillingNotAllowedError extends Error {
  constructor(
    message = 'Invoice billing is not available after unpaid cancellation',
  ) {
    super(message);
    this.name = 'InvoiceBillingNotAllowedError';
  }
}

/** Throws if the current subscription must resubscribe with direct payment only. */
export function assertCanCreateInvoiceSubscription(
  subscription?: UserSubscription | null,
): void {
  if (mustResubscribeWithDirectPayment(subscription)) {
    throw new InvoiceBillingNotAllowedError();
  }
}

export function getStripeSubscriptionEndIso(
  subscription?: UserSubscription | null,
): string | null | undefined {
  const sub = subscription;
  if (!sub) return undefined;

  // Cancelled for non-payment / no remaining access: never surface a date.
  if (isCancelledWithoutRemainingAccess(sub)) {
    return undefined;
  }

  if (isStripeSubscriptionFullyEnded(sub)) {
    return (
      sub.stripeEndedAt ||
      sub.stripeCancelAt ||
      sub.stripeCurrentPeriodEnd ||
      sub.endDate ||
      undefined
    );
  }

  if (
    sub.stripeSubscriptionStatus === 'active' &&
    sub.stripeCancelAtPeriodEnd
  ) {
    return sub.stripeCancelAt || sub.stripeCurrentPeriodEnd || undefined;
  }

  return sub.stripeCurrentPeriodEnd || sub.endDate || undefined;
}

export function isStripeNonRenewingActive(
  subscription?: UserSubscription | null,
  isPaidActive = false,
): boolean {
  return Boolean(
    isPaidActive &&
      subscription?.stripeSubscriptionStatus === 'active' &&
      subscription?.stripeCancelAtPeriodEnd === true,
  );
}

export function isChfSubscriptionCurrency(
  subscription?: UserSubscription | null,
): boolean {
  const currency = (
    subscription?.stripePriceCurrency ||
    subscription?.subscriptionPlan?.currency ||
    ''
  ).toUpperCase();
  return currency === 'CHF';
}

export function isInvoiceBillingLayout(
  subscription?: UserSubscription | null,
  showBillingPaymentDetails = false,
): boolean {
  if (!showBillingPaymentDetails || !subscription) return false;
  if (subscription.stripeCollectionMethod === 'send_invoice') return true;
  if (subscription.enterpriseInvoiceRequest?.contactEmail) return true;
  return false;
}

export function isEnterprisePaidPlan(
  subscription?: UserSubscription | null,
  isPaidActive = false,
): boolean {
  if (!isPaidActive || !subscription) return false;
  const label = `${subscription.subscriptionPlan?.productName ?? ''} ${
    subscription.subscriptionPlan?.name ?? ''
  }`;
  if (/enterprise/i.test(label)) return true;
  if (
    subscription.enterpriseInvoiceRequest ||
    subscription.enterpriseInvoiceRequestId
  ) {
    return true;
  }
  return false;
}
